import { Copy } from '@phosphor-icons/react';
import { Button, ChoiceGroup, Heading, Surface, Text, TextArea, TextField } from '@shh/ui';
import { useState } from 'react';
import { adminSetPlan, friendlyError, type AdminOverview as Overview } from '../data/api';
import { adminText as t } from '../i18n/admin';

/**
 * The read-only part of /admin (after 1.18, Mike 12.10.): numbers, waitlist funnel, families,
 * accounts. Only counts and days arrive here – the server never sends content.
 */
export function AdminNumbers({ data }: { data: Overview }) {
  const n = data.numbers;
  const tiles = (Object.keys(t.numbers) as (keyof typeof t.numbers)[]).map((k) => ({
    label: t.numbers[k],
    value: n[k],
  }));
  return (
    <section aria-labelledby="admin-numbers" className="flex flex-col gap-gap-md">
      <Heading level={2} size="lg" id="admin-numbers">
        {t.numbersTitle}
      </Heading>
      <ul className="grid grid-cols-2 gap-gap-sm sm:grid-cols-3 lg:grid-cols-4">
        {tiles.map((x) => (
          <li key={x.label}>
            <Surface className="flex h-full flex-col gap-gap-xs">
              <span className="text-2xl font-bold tabular-nums">{x.value}</span>
              <span className="text-sm text-text-secondary">{x.label}</span>
            </Surface>
          </li>
        ))}
      </ul>
      {Object.keys(n.feedback30).length > 0 && (
        <Text tone="secondary">
          {t.feedback30}:{' '}
          {Object.entries(n.feedback30)
            .map(([k, v]) => `${v} ${t.feedbackKind[k] ?? k}`)
            .join(' · ')}
        </Text>
      )}
    </section>
  );
}

const list = (r: Record<string, number>) =>
  Object.entries(r)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${k}: ${v}`)
    .join(' · ') || '–';

export function AdminFunnel({ data }: { data: Overview }) {
  const f = data.funnel;
  return (
    <div className="flex flex-col gap-gap-xs">
      <Heading level={3} size="md">
        {t.funnelTitle}
      </Heading>
      <Text>{t.funnel(f.unconfirmed, f.confirmed, f.invited, f.joined)}</Text>
      <Text tone="secondary">
        {t.bySource}: {list(f.bySource)}
      </Text>
      <Text tone="secondary">
        {t.byLocale}: {list(f.byLocale)}
      </Text>
    </div>
  );
}

export function AdminFamilies({ data, onChanged }: { data: Overview; onChanged?: () => void }) {
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  // Plus by hand (1.21): for testing and exceptions until payment exists; test families keep theirs
  const togglePlus = async (f: Overview['families'][number]) => {
    setBusy(f.id);
    setError(undefined);
    try {
      await adminSetPlan(f.id, f.plus ? 'free' : 'plus');
      onChanged?.();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <section aria-labelledby="admin-families" className="flex flex-col gap-gap-md">
      <Heading level={2} size="lg" id="admin-families">
        {t.familiesTitle} ({data.families.length})
      </Heading>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      {data.families.length === 0 ? (
        <Text>{t.noFamilies}</Text>
      ) : (
        <Surface>
          <ul className="flex flex-col divide-y divide-border-subtle">
            {data.families.map((f) => (
              <li key={f.id} className="flex flex-col gap-gap-xs py-gap-sm">
                <span className="flex flex-wrap items-center gap-gap-sm">
                  <span className="font-semibold break-all">{f.name}</span>
                  {f.testFamily && (
                    <span className="rounded-chip bg-feedback-success-surface px-inset-xs text-sm font-semibold">
                      {t.testFamily}
                    </span>
                  )}
                  {(f.plus || f.free) && (
                    <span className="rounded-chip bg-accent-secondary-soft px-inset-xs text-sm font-semibold">
                      {f.plus ? t.plus : t.free}
                    </span>
                  )}
                  {!f.testFamily && (
                    <Button
                      variant="secondary"
                      isDisabled={busy === f.id}
                      onPress={() => void togglePlus(f)}
                    >
                      {f.plus ? t.plusOff : t.plusOn}
                    </Button>
                  )}
                </span>
                <span className="text-sm">{t.family(f)}</span>
                <span className="text-sm text-text-secondary">
                  {t.created} {t.day(f.created)} · {t.lastActive} {t.day(f.lastActive)}
                </span>
              </li>
            ))}
          </ul>
        </Surface>
      )}
    </section>
  );
}

type Account = Overview['accounts'][number];
type Filters = {
  verified: 'all' | 'yes' | 'no';
  active: 'all' | 'd7' | 'd30' | 'older';
  group: 'all' | 'test' | 'normal' | 'none';
  role: 'all' | 'parent' | 'teen';
};
const ALL: Filters = { verified: 'all', active: 'all', group: 'all', role: 'all' };

/** Days since a "YYYY-MM-DD" in household time – the overview only ever gets days. */
function daysSince(day: string | null) {
  if (!day) return Infinity;
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' });
  return (Date.parse(today) - Date.parse(day)) / 86_400_000;
}

/** Every filter must fit (Mike, 13.10.: Status, Gruppe, Berechtigung combinable). */
export function matches(a: Account, f: Filters, q: string) {
  const days = daysSince(a.lastActive);
  return (
    a.email.includes(q.trim().toLowerCase()) &&
    (f.verified === 'all' || a.verified === (f.verified === 'yes')) &&
    (f.active === 'all' ||
      (f.active === 'd7' && days <= 7) ||
      (f.active === 'd30' && days <= 30) ||
      (f.active === 'older' && days > 30)) &&
    (f.group === 'all' ||
      (f.group === 'none' && !a.familyId) ||
      (f.group === 'test' && a.testFamily) ||
      (f.group === 'normal' && !!a.familyId && !a.testFamily)) &&
    (f.role === 'all' || a.role === f.role)
  );
}

/**
 * Accounts grouped by family, with filters and "Adressen kopieren" for exactly the filtered
 * addresses (1.18.1, Mike 13.10.). For running the service only – no newsletter without consent.
 */
export function AdminAccounts({ data }: { data: Overview }) {
  const [q, setQ] = useState('');
  const [f, setF] = useState<Filters>(ALL);
  const [copied, setCopied] = useState<string>();
  const [fallback, setFallback] = useState<string>();
  const set =
    <K extends keyof Filters>(k: K) =>
    (v: Filters[K]) => {
      setCopied(undefined);
      setFallback(undefined);
      setF({ ...f, [k]: v });
    };
  const shown = data.accounts.filter((a) => matches(a, f, q));
  const groups = [...new Set(shown.map((a) => a.familyId ?? ''))]
    .map((id) => {
      const accounts = shown.filter((a) => (a.familyId ?? '') === id);
      return { id, name: accounts[0]!.family, test: accounts[0]!.testFamily, accounts };
    })
    // families by name, "Noch ohne Familie" last
    .sort((x, y) => (!x.name ? 1 : !y.name ? -1 : x.name.localeCompare(y.name, 'de')));
  const fl = t.filters;
  const copy = async () => {
    const list = shown.map((a) => a.email).join(', ');
    try {
      await navigator.clipboard.writeText(list);
      setCopied(t.copied(shown.length));
      setFallback(undefined);
    } catch {
      setFallback(list);
    }
  };
  return (
    <section aria-labelledby="admin-accounts" className="flex flex-col gap-gap-md">
      <Heading level={2} size="lg" id="admin-accounts">
        {t.accountsTitle} ({data.accounts.length})
      </Heading>
      <TextField label={t.search} type="search" value={q} onChange={setQ} />
      <div className="grid gap-gap-md sm:grid-cols-2">
        <ChoiceGroup
          label={fl.verified.label}
          value={f.verified}
          onChange={set('verified')}
          choices={(['all', 'yes', 'no'] as const).map((v) => ({
            value: v,
            label: fl.verified[v],
          }))}
        />
        <ChoiceGroup
          label={fl.active.label}
          value={f.active}
          onChange={set('active')}
          choices={(['all', 'd7', 'd30', 'older'] as const).map((v) => ({
            value: v,
            label: fl.active[v],
          }))}
        />
        <ChoiceGroup
          label={fl.group.label}
          value={f.group}
          onChange={set('group')}
          choices={(['all', 'test', 'normal', 'none'] as const).map((v) => ({
            value: v,
            label: fl.group[v],
          }))}
        />
        <ChoiceGroup
          label={fl.role.label}
          value={f.role}
          onChange={set('role')}
          choices={(['all', 'parent', 'teen'] as const).map((v) => ({
            value: v,
            label: fl.role[v],
          }))}
        />
      </div>
      <div className="flex flex-wrap items-center gap-gap-md">
        <Text tone="secondary">{t.shown(shown.length, data.accounts.length)}</Text>
        <Button icon={Copy} isDisabled={shown.length === 0} onPress={() => void copy()}>
          {t.copy(shown.length)}
        </Button>
      </div>
      <Text tone="secondary" className="text-sm">
        {t.copyNote}
      </Text>
      {copied && (
        <p
          role="status"
          className="rounded-control bg-feedback-success-surface p-inset-sm font-semibold"
        >
          {copied}
        </p>
      )}
      {fallback && <TextArea label={t.copyFallback} value={fallback} isReadOnly rows={3} />}
      {groups.length === 0 ? (
        <Text>{t.noAccounts}</Text>
      ) : (
        groups.map((g) => (
          <Surface key={g.id || 'none'} className="flex flex-col gap-gap-xs">
            <Heading level={3} size="md" className="flex flex-wrap items-center gap-gap-sm">
              {g.name ?? t.noFamilyGroup}
              {g.test && (
                <span className="rounded-chip bg-feedback-success-surface px-inset-xs text-sm font-semibold">
                  {t.testFamily}
                </span>
              )}
            </Heading>
            <ul className="flex flex-col divide-y divide-border-subtle">
              {g.accounts.map((a) => (
                <li key={a.email} className="flex flex-col gap-gap-xs py-gap-sm">
                  <span className="font-semibold break-all">{a.email}</span>
                  <span className="text-sm">
                    {[a.role ? t.role[a.role] : null, a.provider, a.verified ? null : t.unverified]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <span className="text-sm text-text-secondary">
                    {t.created} {t.day(a.created)} · {t.lastActive} {t.day(a.lastActive)}
                  </span>
                </li>
              ))}
            </ul>
          </Surface>
        ))
      )}
    </section>
  );
}
